import {
  BlockFooter,
  BlockTitle,
  List,
  ListButton,
  ListInput,
  ListItem,
  Navbar,
  NavRight,
  Page,
  PageContent,
  Link as F7Link,
  f7,
} from 'framework7-react';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useRef } from 'react';
import { useWatch, type FieldPathByValue } from 'react-hook-form';
import { useErrorT, useT } from '@/i18n';
import { ApiError, apiErrorTextTraced, resolveErrorText } from '@/api/errors';
import { isProdDeploy } from '@/env';
import { useContractForm, useListInput } from '@/lib/forms/useContractForm';
import {
  connectBody,
  connectDefaults,
  connectSchema,
  hasTypedCredentials,
  savedActivation,
  type ConnectProviderValues,
} from '../lib/connect-provider-form';
import type {
  OnlinePaymentProvider,
  PaymentProviderConfig,
  PaymentProviderMode,
} from '@restaurant/api-contracts';
import {
  useConnectPaymentProvider,
  useDisconnectPaymentProvider,
  usePaymentProviders,
  useSetProviderActivation,
} from '../hooks/usePaymentProviders';

// Operators connect their OWN provider account so online payments settle to them.
// Secrets are write-only: the fields start empty and are never pre-filled from a
// saved config (the API never returns them). Operator language only - no raw
// enum/methodId is shown (FRONTEND_PRACTICES Rule 11).
//
// Slide-in editor page, not a bottom sheet (FRONTEND_PRACTICES Rule 12). Reached
// at `/settings/payments/connect/:provider` - `new` for a fresh connect, or a
// provider enum to re-connect that one (defaults pre-selected; secrets re-entered).

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2500, position: 'bottom' });

const isProvider = (v: string): v is OnlinePaymentProvider => v === 'PAYPAL' || v === 'PAYCAT';

const isMode = (v: string | undefined): v is PaymentProviderMode => v === 'SANDBOX' || v === 'LIVE';

function useConnectProviderForm(
  editingProvider: OnlinePaymentProvider | null,
  routeMode: PaymentProviderMode | null,
  providers: PaymentProviderConfig[],
) {
  const t = useT();
  return useContractForm(
    connectSchema({
      noWhitespace: t.payments.errNoWhitespace,
      clientIdEmail: t.payments.errClientIdEmail,
      invalidUrl: t.payments.errInvalidUrl,
    }),
    {
      // Which credential row (mode) the operator is entering/saving. Both modes are
      // selectable everywhere now - the settings-page banner is the safety net, not a
      // hard lock. Seeded from the row that was opened; without one, LIVE on the
      // production host and SANDBOX elsewhere.
      defaultValues: connectDefaults(
        editingProvider ?? 'PAYPAL',
        routeMode ?? (isProdDeploy ? 'LIVE' : 'SANDBOX'),
      ),
      toBody: (values: ConnectProviderValues) => connectBody(values, providers),
    },
  );
}

type ConnectProviderForm = ReturnType<typeof useConnectProviderForm>;

export default function ConnectProviderFormPage({
  f7route,
}: {
  f7route: { params: { provider?: string }; query?: { mode?: string } };
}) {
  const raw = f7route.params.provider;
  const editingProvider = raw && isProvider(raw) ? raw : null;
  // The row the operator tapped on the settings page (Test vs Live). Without it
  // the form falls back to the host default, so a Test row could be saved as LIVE.
  const routeMode = isMode(f7route.query?.mode) ? f7route.query.mode : null;
  const t = useT();
  const { data: providers } = usePaymentProviders();
  const submitRef = useRef<() => void>(() => {});
  const form = useConnectProviderForm(editingProvider, routeMode, providers ?? []);
  // Save lights up only once the form has enough entered to submit.
  const canSave = hasTypedCredentials(useWatch({ control: form.control, name: 'credentials' }));

  // A provider can now hold up to two rows (one per mode). "Editing" = at least
  // one of them exists; the title reflects that, the mode selector inside picks
  // which row is being entered.
  const providerRows = editingProvider
    ? (providers?.filter((p) => p.provider === editingProvider) ?? [])
    : [];
  const back = () => f7.views.current.router.back();

  return (
    <Page name="connect-provider-form" pageContent={false}>
      <Navbar
        title={providerRows.length ? t.payments.updateTitle : t.payments.connectTitle}
        backLink="Back"
      >
        <NavRight>
          <F7Link
            className={canSave ? undefined : 'disabled'}
            onClick={canSave ? () => submitRef.current() : undefined}
          >
            {t.saveShort}
          </F7Link>
        </NavRight>
      </Navbar>
      <ConnectProviderContent
        form={form}
        providers={providers ?? []}
        onDone={back}
        submitRef={submitRef}
      />
    </Page>
  );
}

function ConnectProviderContent({
  form,
  providers,
  onDone,
  submitRef,
}: {
  form: ConnectProviderForm;
  providers: PaymentProviderConfig[];
  onDone: () => void;
  submitRef: { current: () => void };
}) {
  const t = useT();
  const connect = useConnectPaymentProvider();
  const disconnect = useDisconnectPaymentProvider();
  const setActivation = useSetProviderActivation();
  const errorMessages = useErrorT();

  const [provider, mode, chosenActivate] = useWatch({
    control: form.control,
    name: ['credentials.provider', 'mode', 'activate'],
  });
  const clientIdInput = useListInput(form.control, 'credentials.clientId');
  const clientSecretInput = useListInput(form.control, 'credentials.clientSecret');
  const baseUrlInput = useListInput(form.control, 'credentials.baseUrl');
  const tenantIdInput = useListInput(form.control, 'credentials.tenantId');
  const tenantSecretInput = useListInput(form.control, 'credentials.tenantSecret');
  // Synchronous in-flight guard: the Save link isn't a real disabled button
  // (only a CSS class), so a rapid double-tap would otherwise fire connect twice.
  const submittingRef = useRef(false);

  const markFields = (fields: FieldPathByValue<ConnectProviderValues, string>[]) => {
    for (const field of fields) form.setError(field, { message: t.payments.errCheck });
  };

  const handleConnectError = (e: unknown) => {
    const code = e instanceof ApiError ? e.code : null;
    if (code === 'payment.providerAuthFailed') {
      markFields(
        provider === 'PAYPAL'
          ? ['credentials.clientId', 'credentials.clientSecret']
          : ['credentials.tenantId', 'credentials.tenantSecret'],
      );
    } else if (code === 'payment.providerHostModeMismatch') {
      markFields(['credentials.baseUrl']);
    }
    toast(apiErrorTextTraced(e, errorMessages, t.payments.connectFailed));
  };

  // The saved row for the CURRENTLY selected provider+mode (if any). Drives the
  // "keys already saved" note, the activate toggle's initial state, and whether
  // Disconnect is available - all scoped to this one mode.
  const currentModeCfg = providers.find((p) => p.provider === provider && p.mode === mode) ?? null;

  // The row (any provider/mode) the storefront transacts in right now, if any.
  const activeRow = providers.find((p) => p.isActive) ?? null;

  // Mirror the activate toggle to whichever mode row is selected: an already-saved
  // mode shows its real active state. For an UNSAVED mode, default ON only when
  // nothing is active yet (first-time setup) — never default ON when something is
  // already live, so entering sandbox keys to test can't silently steal the
  // storefront away from a live provider. Secrets are NEVER restored.
  const activate = chosenActivate ?? savedActivation(providers, provider, mode);

  // Turning THIS sandbox mode into the active one while a LIVE row is currently
  // taking money is a downgrade: the storefront stops charging real money. Gate
  // it behind a confirm so it can't happen by accident.
  const isDowngrade = (nextActive: boolean) =>
    nextActive && mode === 'SANDBOX' && activeRow?.mode === 'LIVE';

  const confirmDowngrade = (onConfirm: () => void) => {
    f7.dialog.confirm(t.payments.downgradeText, t.payments.downgradeTitle, onConfirm);
  };

  const doConnect = (body: Parameters<typeof connect.mutate>[0], afterConnect: () => void) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    form.clearErrors();
    connect.mutate(body, {
      onSuccess: () => {
        toast(t.payments.connectedToast);
        afterConnect();
      },
      onError: handleConnectError,
      onSettled: () => {
        submittingRef.current = false;
      },
    });
  };

  const submit = form.handleSubmit((body) => {
    const run = () => doConnect(body, onDone);
    if (isDowngrade(body.activate)) confirmDowngrade(run);
    else run();
  });
  submitRef.current = () => void submit();

  const clearCurrent = () => {
    if (provider === 'PAYPAL') {
      form.setValue('credentials.clientId', '');
      form.setValue('credentials.clientSecret', '');
    } else {
      form.setValue('credentials.baseUrl', '');
      form.setValue('credentials.tenantId', '');
      form.setValue('credentials.tenantSecret', '');
    }
  };

  const showRow = (next: { provider: OnlinePaymentProvider; mode: PaymentProviderMode }) => {
    form.setValue('credentials.provider', next.provider);
    form.setValue('mode', next.mode);
    form.setValue('activate', null);
    form.clearErrors();
  };

  // Switching provider would abandon what's typed - confirm save vs discard first.
  const selectProvider = (next: OnlinePaymentProvider) => {
    if (next === provider) return;
    if (!hasTypedCredentials(form.getValues('credentials'))) {
      showRow({ provider: next, mode });
      return;
    }
    const currentName = provider === 'PAYPAL' ? t.payments.paypalChoice : t.payments.paycatChoice;
    f7.dialog
      .create({
        title: t.payments.unsavedTitle,
        text: t.payments.unsavedText(currentName),
        verticalButtons: true,
        buttons: [
          {
            text: t.payments.save,
            strong: true,
            onClick: () => {
              form.handleSubmit((body) => {
                const run = () =>
                  doConnect(body, () => {
                    clearCurrent();
                    showRow({ provider: next, mode });
                  });
                if (isDowngrade(body.activate)) confirmDowngrade(run);
                else run();
              })();
            },
          },
          {
            text: t.payments.discardSwitch,
            color: 'red',
            onClick: () => {
              clearCurrent();
              showRow({ provider: next, mode });
            },
          },
          { text: t.payments.cancel, color: 'gray' },
        ],
      })
      .open();
  };

  // Disconnect only makes sense when the SELECTED provider+mode is actually saved
  // - it deletes just that mode's row; the other mode is untouched.
  const canDisconnect = Boolean(currentModeCfg);

  const confirmDisconnect = () => {
    if (!currentModeCfg) return;
    f7.dialog.confirm(t.payments.disconnectConfirm, t.payments.disconnect, () => {
      disconnect.mutate(
        { provider, mode },
        {
          onSuccess: () => {
            toast(t.payments.disconnectedToast);
            onDone();
          },
          onError: (e) => toast(resolveErrorText(e, errorMessages)),
        },
      );
    });
  };

  return (
    <PageContent>
      <BlockTitle>{t.payments.providerLabel}</BlockTitle>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem
          radio
          radioIcon="end"
          name="payment-provider"
          title={t.payments.paypalChoice}
          checked={provider === 'PAYPAL'}
          onChange={() => selectProvider('PAYPAL')}
        />
        <ListItem
          radio
          radioIcon="end"
          name="payment-provider"
          title={t.payments.paycatChoice}
          checked={provider === 'PAYCAT'}
          onChange={() => selectProvider('PAYCAT')}
        />
      </List>

      {/* Test vs Live key set. Both are selectable everywhere - the settings
          banner warns when the sandbox set is the one taking checkout. */}
      <BlockTitle>{t.payments.modeSectionTitle}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{t.payments.modeSectionFooter}</BlockFooter>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem
          radio
          radioIcon="end"
          name="payment-mode"
          title={t.payments.modeTest}
          checked={mode === 'SANDBOX'}
          onChange={() => showRow({ provider, mode: 'SANDBOX' })}
        />
        <ListItem
          radio
          radioIcon="end"
          name="payment-mode"
          title={t.payments.modeLive}
          checked={mode === 'LIVE'}
          onChange={() => showRow({ provider, mode: 'LIVE' })}
        />
      </List>
      <BlockFooter style={{ marginTop: -6 }}>
        {currentModeCfg ? `⚠️ ${t.payments.modeHasCreds}` : t.payments.modeNoCreds}
      </BlockFooter>

      {provider === 'PAYPAL' ? (
        <>
          <BlockTitle>{t.payments.paypalCredsTitle}</BlockTitle>
          <BlockFooter style={{ marginTop: -6 }}>{t.payments.paypalFooter}</BlockFooter>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={t.payments.clientId}
              type="text"
              placeholder={t.payments.clientIdPlaceholder}
              outline={f7.theme !== 'ios'}
              floatingLabel={f7.theme !== 'ios'}
              {...clientIdInput}
            />
            <ListInput
              label={t.payments.secret}
              type="password"
              placeholder={t.payments.secretPlaceholder}
              outline={f7.theme !== 'ios'}
              floatingLabel={f7.theme !== 'ios'}
              {...clientSecretInput}
            />
          </List>
          {/* Webhook is registered automatically on connect (backend calls PayPal
              with these credentials), so there's no Webhook ID to paste here. */}
          <BlockFooter>{t.payments.webhookFooter}</BlockFooter>
        </>
      ) : (
        <>
          <BlockTitle>{t.payments.paycatCredsTitle}</BlockTitle>
          <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
            <ListInput
              label={t.payments.serviceUrl}
              type="url"
              placeholder="https://…"
              outline={f7.theme !== 'ios'}
              floatingLabel={f7.theme !== 'ios'}
              {...baseUrlInput}
            />
            <ListInput
              label={t.payments.tenantId}
              type="text"
              outline={f7.theme !== 'ios'}
              floatingLabel={f7.theme !== 'ios'}
              {...tenantIdInput}
            />
            <ListInput
              label={t.payments.tenantSecret}
              type="password"
              outline={f7.theme !== 'ios'}
              floatingLabel={f7.theme !== 'ios'}
              {...tenantSecretInput}
            />
          </List>
        </>
      )}

      <BlockTitle>{t.payments.settingsTitle}</BlockTitle>
      <BlockFooter style={{ marginTop: -6 }}>{t.payments.settingsFooter}</BlockFooter>
      <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
        <ListItem title={t.payments.useForCheckout} footer={t.payments.useForCheckoutHelp}>
          <ToggleSwitch
            slot="after"
            checked={activate}
            onChange={(v) => {
              // Unsaved mode: the toggle is just the connect payload's `activate`.
              if (!currentModeCfg) {
                form.setValue('activate', v);
                return;
              }
              // Already-saved mode: persist immediately via the activation
              // endpoint (no credentials needed). Revert the switch if it fails.
              const persist = () => {
                form.setValue('activate', v);
                setActivation.mutate(
                  { provider, mode, isActive: v },
                  {
                    onSuccess: () => {
                      form.setValue('activate', null);
                      toast(v ? t.payments.activatedToast : t.payments.deactivatedToast);
                    },
                    onError: (e) => {
                      form.setValue('activate', null);
                      toast(resolveErrorText(e, errorMessages));
                    },
                  },
                );
              };
              // Switching the storefront from live to sandbox stops real payments
              // — confirm before flipping (leave the toggle as-is until confirmed).
              if (isDowngrade(v)) confirmDowngrade(persist);
              else persist();
            }}
          />
        </ListItem>
      </List>

      <List strongIos strongMd insetIos insetMd>
        <ListButton
          color="red"
          className={canDisconnect ? undefined : 'disabled'}
          onClick={canDisconnect ? confirmDisconnect : undefined}
        >
          {t.payments.disconnect}
        </ListButton>
      </List>
    </PageContent>
  );
}
